import { useState } from "react";
import type { CaseRecord } from "../../types";
import { formatDate } from "../../lib/formatDate";

// Provenance note shown on a deduplicated fact: "also stated in N records"
// with the source dates, expanding to the full list of source records (each
// clickable to navigate). Round 11 (Option B/C): replaces the old "also in N
// OTHER records" note. The point is corroboration made VISIBLE — overlapping
// SMS/Plaud exports each carry the shared fact, and surfacing every source +
// date shows the fact was stated across them, rather than hiding the
// duplicates. These exports are INDEPENDENT (no proven containment — see
// dedupeFacts.ts), so the note is flat corroboration, NOT a version chain.
// Counts ALL sources (the representative included). Renders nothing for a
// unique fact (a single source needs no provenance).
//
// `recordIds` is the full set of source records for the fact (representative +
// alsoIn); callers pass `[fact.record_id, ...alsoIn]`.
export default function FactProvenance({
  recordIds,
  recordById,
  onNavigateToRecord,
}: {
  recordIds: string[];
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const sources = [...new Set(recordIds)]
    .map((id) => {
      const rec = recordById.get(id);
      return { id, rec, day: (rec?.record_date ?? "").slice(0, 10) };
    })
    .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));

  if (sources.length <= 1) return null;

  const dateList = sources
    .map((s) => (s.day ? formatDate(s.day, "short") || s.day : "—"))
    .join(", ");

  return (
    <div className="fact-also-in">
      <button
        type="button"
        className="fact-also-in-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? "▾" : "▸"} also stated in {sources.length} records · {dateList}
      </button>
      {open && (
        <ul className="fact-also-in-list">
          {sources.map((s) => {
            const label =
              s.rec?.subject?.trim() ||
              (s.rec
                ? `${s.rec.source_type} — ${s.rec.from_addr ?? "?"}`
                : "record");
            const when = s.day ? formatDate(s.day, "short") || s.day : "";
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className="fact-source-link"
                  onClick={() => onNavigateToRecord?.(s.id)}
                >
                  {when ? `${when} — ${label}` : label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
